"use client";

import { useEffect, useState } from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import {
	dispatchFavoritesUpdated,
	getDefaultFavoriteToken,
	mergeFavoriteTokensWithDefaults,
	fetchFavoriteTokensForPrincipal,
	readGuestFavoriteTokens,
	subscribeFavoritesUpdated,
	type FavoriteTokenSummary,
} from "@/lib/favorites/favorites";

const uiLog = createLogAdapter("hooks/useUnifiedFavoriteTokens.ts");

export function useUnifiedFavoriteTokens(
	principal: string,
	options?: {
		isAuthenticated?: boolean;
		enabled?: boolean;
		limit?: number;
		includeDefaultBabyOdin?: boolean;
	},
) {
	const enabled = options?.enabled ?? true;
	const isAuthenticated = options?.isAuthenticated ?? false;
	const limit = options?.limit ?? 5;
	const includeDefaultBabyOdin = options?.includeDefaultBabyOdin ?? true;
	const [favorites, setFavorites] = useState<FavoriteTokenSummary[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!enabled) {
			setFavorites([]);
			setLoading(false);
			return;
		}

		let cancelled = false;

		const loadFavorites = async () => {
			try {
				setLoading(true);
				const baseFavorites =
					!isAuthenticated || !principal
						? readGuestFavoriteTokens()
						: await fetchFavoriteTokensForPrincipal(principal, limit);
				let nextFavorites = baseFavorites.slice(0, limit);

				if (includeDefaultBabyOdin) {
					const defaultBabyOdin = await getDefaultFavoriteToken();
					nextFavorites = mergeFavoriteTokensWithDefaults(
						nextFavorites,
						[defaultBabyOdin],
						limit,
					);
				}

				if (!cancelled) {
					setFavorites(nextFavorites);
				}
			} catch (error) {
				uiLog.error("Error loading unified favorites:", error);
				if (!cancelled) {
					setFavorites([]);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		void loadFavorites();

		const unsubscribe = subscribeFavoritesUpdated((detail) => {
			if (detail.source === "guest" && !isAuthenticated) {
				void loadFavorites();
				return;
			}

			if (
				detail.source === "server" &&
				isAuthenticated &&
				detail.principal === principal
			) {
				void loadFavorites();
			}
		});

		return () => {
			cancelled = true;
			unsubscribe();
		};
	}, [enabled, includeDefaultBabyOdin, isAuthenticated, limit, principal]);

	return { favorites, loading, refreshFavorites: () => {
		dispatchFavoritesUpdated({
			source: isAuthenticated ? "server" : "guest",
			principal: isAuthenticated ? principal : undefined,
		});
	} };
}
